import React, { Fragment, useCallback, useState } from 'react'
import { Button } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import {
  Confirm,
  RaRecord,
  useSaveContext,
  setSubmissionErrors,
  TransformData,
  useTranslate,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { SaveWithConfirmButtonProps, IValueOrDefault } from './type'

const valueOrDefault: IValueOrDefault = (value, defaultValue) =>
  typeof value === 'undefined' ? defaultValue : value

export const SaveWithConfirmButton: React.FC<SaveWithConfirmButtonProps> = (props) => {
  const {
    className,
    color = 'primary',
    confirmTitle,
    confirmContent,
    icon = <SaveIcon />,
    label = 'ra.action.save',
    transform: transformProps = (data): Partial<RaRecord> => data,
    encType = 'application/json',
    alwaysEnable = false,
    disabled: disabledProp = false,
    ...rest
  } = props
  const translate = useTranslate()
  const defaultMessage = {
    title: translate((confirmTitle as string) || 'ra.message.bulk_update_title', {
      smart_count: 1,
    }),
    content: translate(confirmContent || 'ra.message.bulk_update_content', { smart_count: 1 }),
  }
  const [open, setOpen] = useState(false)
  const { getValues, formState, setError, handleSubmit } = useFormContext()
  const { save, saving } = useSaveContext()
  const { isDirty, isValidating, isSubmitting } = formState
  // Use form isDirty, isValidating and form context saving to enable or disable the save button
  // if alwaysEnable is undefined
  const disabled = valueOrDefault(
    alwaysEnable === false || alwaysEnable === undefined ? undefined : !alwaysEnable,
    disabledProp || !isDirty || isValidating || saving || isSubmitting,
  )

  const transform = useCallback(
    (data): TransformData => {
      const transformedData = transformProps(data)
      return {
        ...transformedData,
        encType: encType,
      }
    },
    [encType, transformProps],
  )

  const handleSave = useCallback(async () => {
    let errors
    const data = getValues()
    if (save) {
      errors = await save(data, {
        transform,
      })
    }
    if (errors != null) {
      setSubmissionErrors(errors, setError)
    }
  }, [getValues, save, transform, setError])

  const handleInvalid = useCallback(() => {
    setOpen(false)
  }, [])

  const handleConfirm = useCallback(
    async (e): Promise<void> => {
      e.stopPropagation()
      await handleSubmit(handleSave, handleInvalid)(e)
    },
    [handleInvalid, handleSave, handleSubmit],
  )

  const handleDialogOpen = useCallback((): void => {
    setOpen(true)
  }, [])

  const handleDialogClose = useCallback((): void => {
    setOpen(false)
  }, [])
  return (
    <Fragment>
      <Button
        disabled={disabled}
        type='button'
        color={color}
        onClick={handleDialogOpen}
        className={`ra-save-button ${className}`}
        startIcon={icon}
        size='medium'
        variant='contained'
        {...rest}
      >
        {translate(label)}
      </Button>
      <Confirm
        isOpen={open}
        loading={saving}
        title={defaultMessage.title}
        content={defaultMessage.content}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </Fragment>
  )
}
